import { useCallback, useMemo } from 'react'
import { useProjectStore } from './use-project-store'
import { Project, ProjectMessage } from '@/lib/types'
import { cloneDeep } from 'lodash'
import { useAICoding } from './use-ai-coding'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'

export const useProjectPromptQueue = () => {
  const { project, getProject, updateProject, addMessage, api__appendMessage } =
    useProjectStore()
  const { aiCoding } = useAICoding()
  const t = useTranslations()

  const savePromptQueue = async (promptQueue: Project['prompt_queue']) => {
    updateProject({ prompt_queue: promptQueue })
    await fetch('/api/me/project', {
      method: 'POST',
      body: (() => {
        const formData = new FormData()

        formData.append('projectId', project?._id?.toString() || '')
        formData.append('promptQueue', JSON.stringify(promptQueue || []))

        return formData
      })(),
    }).catch((err) => {
      console.error('Failed to save prompt queue:', err)
    })
  }

  const addToPromptQueue = (message: ProjectMessage) => {
    const promptQueue = cloneDeep(project?.prompt_queue || [])
    promptQueue.push({
      message,
      status: 'pending',
    })
    savePromptQueue(promptQueue)
    toast.success(t('queue.add_queue_toast'))
  }

  const removeFromQueue = (messageId: string) => {
    const promptQueue = cloneDeep(project?.prompt_queue || [])
    const filteredQueue = promptQueue.filter(
      (item) => item.message.id !== messageId,
    )
    savePromptQueue(filteredQueue)
  }

  const pinToTop = (messageId: string) => {
    const promptQueue = cloneDeep(project?.prompt_queue || [])
    const itemIndex = promptQueue.findIndex(
      (item) => item.message.id === messageId,
    )

    if (itemIndex > 0) {
      const [item] = promptQueue.splice(itemIndex, 1)
      promptQueue.unshift(item)
      savePromptQueue(promptQueue)
    }
  }

  const projectId = project?._id?.toString()

  const refreshPromptQueueFromServer = useCallback(async () => {
    if (!projectId) {
      return
    }
    try {
      const response = await fetch(`/api/projects/${projectId}`)
      if (!response.ok) {
        throw new Error('Failed to fetch latest project')
      }
      const latestProject: Project = await response.json()
      updateProject({ prompt_queue: latestProject?.prompt_queue || [] })
    } catch (error) {
      console.error('Failed to refresh prompt queue:', error)
    }
  }, [projectId, updateProject])

  const sortedPromptQueue = useMemo(() => {
    return (
      cloneDeep(project?.prompt_queue || []).sort((a, b) => {
        const statusOrder = { done: 0, processing: 1, pending: 2 }

        // If both items have the same status, maintain original order
        if (a.status === b.status) {
          return 0
        }

        return statusOrder[a.status] - statusOrder[b.status]
      }) || []
    )
  }, [project?.prompt_queue])

  const triggerNextTask = async () => {
    const project = getProject()
    const promptQueue = cloneDeep(project?.prompt_queue || [])
    if (promptQueue.length === 0) {
      return
    }
    const runningTask = promptQueue.find((item) => item.status === 'processing')
    if (runningTask) {
      return
    }
    const nextTaskIndex = promptQueue.findIndex(
      (item) => item.status === 'pending',
    )
    if (nextTaskIndex === -1) {
      return
    }
    const nextTask = promptQueue[nextTaskIndex]
    // remove the task from the queue (also remove other pending tasks for old cases)
    savePromptQueue(
      promptQueue.filter(
        (task, index) => index !== nextTaskIndex || task.status !== 'pending',
      ),
    )
    addMessage(nextTask.message)
    await api__appendMessage(nextTask.message)
    aiCoding({ addedUserMessage: nextTask.message })
  }

  return {
    sortedPromptQueue,
    addToPromptQueue,
    removeFromQueue,
    pinToTop,
    triggerNextTask,
    refreshPromptQueueFromServer,
  }
}
